import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid2,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Stack,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import Header from "../Header";
import Footer from "../Footer";
import CommonDatePicker from "../datepicker/CommonDatePicker";
import { format } from "date-fns";
import config from "../common/Config";
import "../common/commonStyles.css";
import { buildDatatablePayload, postDashboardDatatable } from "../../utils/dashboardApi";

const MaxValueOrder = () => {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [dates, setDates] = useState([null, null]);
  const [totalOrder, setTotalOrder] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [average, setAverage] = useState('');

  const s = localStorage.getItem('startDate');
  const e = localStorage.getItem('endDate');

  const handleDateChange = (value) => {
    setDates(value);
    if (value && value[0] && value[1]) {
      const formattedStartDate = format(value[0], 'yyyy-MM-dd');
      const formattedEndDate = format(value[1], 'yyyy-MM-dd');
      localStorage.setItem('startDate', formattedStartDate);
      localStorage.setItem('endDate', formattedEndDate);
      setPage(0);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { response, data: json } = await postDashboardDatatable(
        token,
        'getCustomerOrder',
        buildDatatablePayload({
          page,
          rowsPerPage,
          startDate: s,
          endDate: e,
          searchValue,
        })
      );

      if (!response?.ok) {
        throw new Error(`Failed to fetch data: ${response?.status ?? 'unknown'}`);
      }

      if (json && Array.isArray(json.data)) {
        setData(json.data);
        setTotalCount(json.recordsTotal || json.recordsFiltered || json.data.length);

        const totalOrderValue = json.totalOrder || json.total_order || json.totalOrderCount || json.total_orders || '';
        const totalAmountValue = json.totalAmount || json.total_amount || json.totalAmountSum || '';
        const averageValue = json.average || json.avg || json.avgAmount || '';

        setTotalOrder(totalOrderValue);
        setTotalAmount(totalAmountValue);
        setAverage(averageValue);

        if (!totalOrderValue && !totalAmountValue && !averageValue && json.data.length > 0) {
          const totalOrders = json.data.reduce((sum, row) => sum + (parseInt(row.total_orders) || 0), 0);
          const totalAmt = json.data.reduce((sum, row) => sum + (parseFloat(row.total_amount) || 0), 0);
          const avg = totalOrders > 0 ? totalAmt / totalOrders : 0;

          setTotalOrder(totalOrders.toString());
          setTotalAmount(totalAmt.toString());
          setAverage(avg.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching max value order data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, s, e, searchValue]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = "/react/login";
      return;
    }
    fetchData();
  }, [fetchData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      setPage(0);
    }
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleSubmit = () => {
    setPage(0);
    fetchData();
  };

  return (
    <>
      <Box className="page-container" >
        <Paper
          className="paper-style"
        >
          <Box sx={{ px:2, py:1.2}}>
            <Grid2 container spacing={1} justifyContent="space-between" alignItems="center">
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography className="page-title">
                  Max Order Value
                </Typography>
              </Grid2>

                <Grid2 size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    className="input-border"
                    size="small"
                    placeholder="Search..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify
                            icon="eva:search-fill"
                            sx={{ color: "#212b36", width: 20, height: 20 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 3}}>
                  <CommonDatePicker
                    className="input-border"
                    value={dates}
                    onChange={handleDateChange}
                    format="dd/MM/yyyy"
                    character=" - "
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 1 }}>
                  <Button
                    variant="contained"
                    className="button-variant-contained"
                    onClick={handleSubmit}
                    color="primary"
                    fullWidth
              
                  >
                    Submit
                  </Button>
                </Grid2>
              </Grid2>
              </Box>

              <Box sx={{ mb: 2, mx:2, p: 2, backgroundColor: "#f5f7fa", borderRadius: 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }} justifyContent="center">
                  <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }} display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "#212b36" }}>
                      Total Order:
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "#212b36", fontWeight: 600 }}>
                      {totalOrder || '0'}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }} display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "#212b36" }}>
                      Total Amount:
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "#212b36", fontWeight: 600 }}>
                      {totalAmount ? Number(totalAmount).toFixed(2) : '0.00'}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }} display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "#212b36" }}>
                      Average:
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "#212b36", fontWeight: 600 }}>
                      {average ? Number(average).toFixed(2) : '0.00'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <TableContainer
      
              >
                {loading && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={40} />
                  </Box>
                )}
                <Table
                  size="small"
                  className="table-cell-border"
                >
                  <TableHead>
                    <TableRow className="table-cell-header">
                      <TableCell
                      width="100px"
                      >
                        #
                      </TableCell>
                      <TableCell
                        align="left"
                        width="100px"
                      >
                        Account No
                      </TableCell>
                      <TableCell
                        align="left"
                        width="200px"
                      >
                        Customer Name
                      </TableCell>
                      <TableCell
                        align="center"
                        width="100px"
                      >
                        Total Orders
                      </TableCell>
                      <TableCell
                        align="right"
                        width="100px"
                      >
                        Total Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!loading && data && data.length > 0 ? (
                      data.map((row, index) => (
                        <TableRow
                          key={index}
                          hover
                          className="table-cell-style"
                        >
                          <TableCell
                          >
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell
                            align="left"
                          >
                            {row.accound_no}
                          </TableCell>
                          <TableCell
                            align="left"
                          >
                            {row.name}
                          </TableCell>
                          <TableCell
                            align="center"
                          >
                            {row.total_orders}
                          </TableCell>
                          <TableCell
                            align="right"
                          >
                            {row.total_amount}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : !loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{ py: 8 }}
                        >
                          <Typography align="center" sx={{ color: "#637381", fontSize: "0.875rem" }}>
                            No data found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>

              {!loading && (
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                  className="pagination-styles"
                />
              )}
        </Paper>
      </Box>
    </>
  );
};

export default MaxValueOrder;
